// ...existing code...
import { useState, useEffect, useCallback } from "react";
import apiClient from "../../context/apiClient";
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Box,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  IconButton,
  Dialog,
  Tabs,
  Tab,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  CircularProgress,
  Paper,
  Stack,
  useTheme,
  Link,
  Skeleton,
  FormControlLabel,
  Switch,
  styled,
  keyframes,
  Pagination,
} from "@mui/material";
import {
  Edit as EditIcon,
  Visibility,
  Schedule,
  Download,
  Star,
  ArrowBack,
  Delete as DeleteIcon,
  Publish as PublishIcon,
  BookmarkBorder as BookmarkBorderIcon,
  Email,
  Phone,
  LocationOn,
} from "@mui/icons-material";
import Layout from "../common/Layout";
import { useNotification } from "../../context/NotificationContext";

const arrowAnimation = keyframes`
  0% {
    opacity: 0;
    transform: translateX(0);
  }
  100% {
    opacity: 1;
    transform: translateX(10px);
  }
`;

const CustomDotIcon = () => (
  <svg
    width={16}
    height={19}
    viewBox="0 0 16 19"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <circle cx="1.61321" cy="1.61321" r="1.5" fill="currentColor" />
    <circle cx="5.73583" cy="1.61321" r="1.5" fill="currentColor" />
    <circle cx="5.73583" cy="5.5566" r="1.5" fill="currentColor" />
    <circle cx="9.85851" cy="5.5566" r="1.5" fill="currentColor" />
    <circle cx="9.85851" cy="9.5" r="1.5" fill="currentColor" />
    <circle cx="13.9811" cy="9.5" r="1.5" fill="currentColor" />
    <circle cx="5.73583" cy="13.4434" r="1.5" fill="currentColor" />
    <circle cx="9.85851" cy="13.4434" r="1.5" fill="currentColor" />
    <circle cx="1.61321" cy="17.3868" r="1.5" fill="currentColor" />
    <circle cx="5.73583" cy="17.3868" r="1.5" fill="currentColor" />
  </svg>
);

const StyledMuiButton = styled(Button)(({ theme }) => ({
  width: "170px",
  height: "51px",
  padding: "0 0 0 20px",
  backgroundColor: "transparent",
  borderRadius: "40px",
  border: `1.5px solid ${theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.4)" : "rgba(0, 0, 0, 0.5)"}`,
  boxShadow: "0px 4px 12px rgba(0,0,0,0.1)",
  justifyContent: "space-between",
  "&:hover:not(.Mui-disabled)": {
    borderColor: theme.palette.primary.main,
    backgroundColor:
      theme.palette.mode === "dark"
        ? "rgba(255, 255, 255, 0.03)"
        : "rgba(0, 0, 0, 0.02)",
  },
  color: theme.palette.text.primary,
  fontSize: "1.1em",
  letterSpacing: "1.2px",
  fontWeight: 600,
  textTransform: "none",
  "&.Mui-disabled": {
    backgroundColor: "rgba(29, 33, 41, 0.08)",
    color: theme.palette.text.disabled,
  },
  ".MuiButton-endIcon": {
    margin: 0,
    width: "42px",
    height: "42px",
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: "50%",
    border: `2px solid ${theme.palette.background.paper}`,
    "& svg": {
      transition: "all 1.5s",
      color: "inherit",
    },
  },
  "&:hover:not(.Mui-disabled) .MuiButton-endIcon svg": {
    animation: `${arrowAnimation} 1s linear infinite`,
  },
}));

function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`job-tabpanel-${index}`}
      aria-labelledby={`job-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

const CandidateManagement = () => {
  const theme = useTheme();
  const { showNotification } = useNotification();
  const [jobPosts, setJobPosts] = useState([]);
  const [loading, setLoading] = useState({
    jobs: true,
    candidates: false,
    details: false,
    postJob: false,
    deleteJob: false,
    potentialCandidates: false,
  });
  const [error, setError] = useState(null);
  const [selectedJobPost, setSelectedJobPost] = useState(null);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [potentialCandidates, setPotentialCandidates] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [filterStatus, setFilterStatus] = useState("all");
  const [interviewData, setInterviewData] = useState({
    date: "",
    time: "",
    type: "video",
    notes: "",
    link: "",
    address: "",
    mapUrl: "",
  });
  const [aiScoreLoadingIds, setAiScoreLoadingIds] = useState([]);
  const [aiScoreFetchError, setAiScoreFetchError] = useState(false);
  const [jobTabValue, setJobTabValue] = useState(0);
  const [openDraftDialog, setOpenDraftDialog] = useState(false);
  const [selectedDraftJob, setSelectedDraftJob] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [editDraftData, setEditDraftData] = useState(null);
  const [savingDraft, setSavingDraft] = useState(false);

  const [postedJobsPage, setPostedJobsPage] = useState(1);
  const [draftJobsPage, setDraftJobsPage] = useState(1);
  const [candidatesPage, setCandidatesPage] = useState(1);
  const [potentialCandidatesPage, setPotentialCandidatesPage] = useState(1);

  const JOBS_PER_PAGE = 8;
  const CANDIDATES_PER_PAGE = 5;
  const POTENTIAL_CANDIDATES_PER_PAGE = 5;

  useEffect(() => {
    setCandidatesPage(1);
  }, [filterStatus]);

  const handleError = useCallback(
    (err, action) => {
      if (err.response?.status === 401) {
        return;
      }
      let message = `Failed to ${action}.`;
      if (!err.response) {
        message = `Network error. Could not ${action}. Please check your connection.`;
      } else if (err.response.data?.detail) {
        message = err.response.data.detail;
      }
      showNotification(message, "error");
      console.error(`Error during "${action}":`, err);
    },
    [showNotification],
  );

  const handleGetAiScore = useCallback(
    async (applicationId) => {
      if (!selectedJobPost?.id) {
        return;
      }
      setAiScoreLoadingIds((prev) => [...new Set([...prev, applicationId])]);
      try {
        const response = await apiClient.get(
          `/job-postings/ai-score-totalent/${selectedJobPost.id}/${applicationId}/`,
        );
        const newScore = Math.round(response.data.matching_percentage ?? 0);
        setSelectedJobPost((prev) => {
          if (!prev) return null;
          return {
            ...prev,
            applications: prev.applications.map((app) =>
              app.id === applicationId ? { ...app, match: newScore } : app,
            ),
          };
        });
      } catch (err) {
        if (!aiScoreFetchError) {
          handleError(err, "fetch AI scores");
          setAiScoreFetchError(true);
        }
        setSelectedJobPost((prev) => {
          if (!prev) return null;
          return {
            ...prev,
            applications: prev.applications.map((app) =>
              app.id === applicationId ? { ...app, match: -1 } : app,
            ),
          };
        });
      } finally {
        setAiScoreLoadingIds((prev) =>
          prev.filter((id) => id !== applicationId),
        );
      }
    },
    [selectedJobPost?.id, handleError, aiScoreFetchError],
  );

  useEffect(() => {
    const fetchJobPosts = async () => {
      try {
        setLoading((prev) => ({ ...prev, jobs: true }));
        setError(null);
        const response = await apiClient.get("/job-postings/");
        const jobs = response.data;
        await Promise.all(
          jobs.map(async (job, idx) => {
            if (typeof job.applications_count !== "number") {
              try {
                const res = await apiClient.get(
                  `/job-postings/${job.id}/applications/`,
                );
                jobs[idx].applications_count = Array.isArray(
                  res.data.applications,
                )
                  ? res.data.applications.length
                  : 0;
              } catch {
                jobs[idx].applications_count = 0;
              }
            }
          }),
        );
        setJobPosts(jobs);
      } catch (err) {
        setError(err.message);
        handleError(err, "load job postings");
      } finally {
        setLoading((prev) => ({ ...prev, jobs: false }));
      }
    };
    fetchJobPosts();
  }, [handleError]);

  useEffect(() => {
    if (selectedJobPost && selectedJobPost.applications.length > 0) {
      selectedJobPost.applications.forEach((app) => {
        if (app.match === null && !aiScoreLoadingIds.includes(app.id)) {
          handleGetAiScore(app.id);
        }
      });
    }
  }, [selectedJobPost?.applications, aiScoreLoadingIds, handleGetAiScore]);

  const fetchApplicationsForJob = useCallback(
    async (jobPostId, jobTitle) => {
      try {
        const response = await apiClient.get(
          `/job-postings/${jobPostId}/applications/`,
        );
        if (!response.data || !Array.isArray(response.data.applications)) {
          showNotification("Received invalid data for applications.", "error");
          return { applications: [], summary_stats: {} };
        }
        const parseJsonArray = (val) => {
          if (!val) return [];
          if (Array.isArray(val)) return val;
          try {
            return JSON.parse(val);
          } catch {
            return [];
          }
        };
        const transformedApplications = response.data.applications.map(
          (app) => {
            const resume =
              Array.isArray(app.talent_full_resume) &&
              app.talent_full_resume.length > 0
                ? app.talent_full_resume[0]
                : {};
            const name =
              resume.name?.trim() ||
              app.applicant_name?.trim() ||
              resume.talent_id?.username ||
              "Not specified";
            const email =
              resume.email ||
              resume.talent_id?.email ||
              app.email ||
              "Not specified";
            const phone =
              resume.phone ||
              resume.talent_id?.phone_number ||
              app.phone ||
              "Not specified";
            const location =
              resume.current_location ||
              resume.preferred_location ||
              app.preferred_location ||
              "Not specified";
            const resumeUrl =
              resume.resume_pdf_url ||
              resume.resume_pdf ||
              app.talent_resume_latest_url ||
              null;
            return {
              id: app.id,
              talent_id: app.talent,
              name,
              email,
              phone,
              location,
              position:
                jobTitle || app.job_posting_details?.title || "Not specified",
              experience: resume.summary || "Not specified",
              skills: parseJsonArray(resume.skills),
              match: app.matching_percentage ?? null,
              status: app.status_display || app.status,
              appliedDate: new Date(app.application_date).toLocaleDateString(),
              resumeUrl,
              portfolioUrl: resume.portfolio_url || "",
              expectedSalary: resume.expected_salary ?? "Not specified",
              availability: resume.availability ?? "Not specified",
              languages: resume.languages ?? [],
              education: resume.degree_name
                ? `${resume.degree_name} (${
                    resume.degree_institution_name || ""
                  })`
                : "Not specified",
              certifications: parseJsonArray(resume.certifications),
              previousCompanies: resume.current_company
                ? [resume.current_company]
                : [],
            };
          },
        );
        const summary = response.data.summary_stats || {};
        if (summary.shortlisted_count === undefined) {
          summary.shortlisted_count = transformedApplications.filter(
            (app) => app.status?.toLowerCase() === "shortlisted",
          ).length;
        }
        return {
          applications: transformedApplications,
          summary_stats: summary,
        };
      } catch (err) {
        handleError(err, "load applications");
        return {
          applications: [],
          summary_stats: {
            total_applications: 0,
            under_review_count: 0,
            interviews_scheduled_count: 0,
            shortlisted_count: 0,
            average_match: "N/A",
          },
        };
      } finally {
        setLoading((prev) => ({ ...prev, candidates: false }));
      }
    },
    [handleError, showNotification],
  );

  const handleSelectJobPost = async (job) => {
    setLoading((prev) => ({
      ...prev,
      candidates: true,
      potentialCandidates: true,
    }));
    setAiScoreFetchError(false);
    setPotentialCandidates([]);
    setCandidatesPage(1);
    setPotentialCandidatesPage(1);
    setSelectedJobPost({
      ...job,
      applications: [],
      summary_stats: {
        total_applications: 0,
        under_review_count: 0,
        interviews_scheduled_count: 0,
        shortlisted_count: 0,
        average_match: "N/A",
      },
    });
    setFilterStatus("all");

    const { applications, summary_stats } = await fetchApplicationsForJob(
      job.id,
      job.title,
    );
    setJobPosts((prev) =>
      prev.map((j) =>
        j.id === job.id ? { ...j, applications_count: applications.length } : j,
      ),
    );
    setSelectedJobPost((prev) => ({
      ...prev,
      applications,
      summary_stats,
      applications_count: applications.length,
    }));

    try {
      const potentialResponse = await apiClient.get(
        `/job-postings/${job.id}/potential-candidates/`,
      );
      const sortedPotential = potentialResponse.data.sort(
        (a, b) => b.ai_match_score - a.ai_match_score,
      );
      setPotentialCandidates(sortedPotential);
    } catch (err) {
      handleError(err, "load potential candidates");
      setPotentialCandidates([]);
    } finally {
      setLoading((prev) => ({ ...prev, potentialCandidates: false }));
    }
  };

  const fetchApplicationDetails = useCallback(
    async (jobPostId, applicationId) => {
      try {
        setLoading((prev) => ({ ...prev, details: true }));
        const response = await apiClient.get(
          `/job-postings/${jobPostId}/applications/${applicationId}/`,
        );
        const app = response.data;
        const parseJsonArray = (val) => {
          if (!val) return [];
          if (Array.isArray(val)) return val;
          try {
            return JSON.parse(val);
          } catch {
            return [];
          }
        };
        const resume =
          Array.isArray(app.talent_full_resume) &&
          app.talent_full_resume.length > 0
            ? app.talent_full_resume[0]
            : {};
        const name =
          resume.name?.trim() ||
          app.applicant_name?.trim() ||
          resume.talent_id?.username ||
          "Not specified";
        const email =
          resume.email ||
          resume.talent_id?.email ||
          app.email ||
          "Not specified";
        const phone =
          resume.phone ||
          resume.talent_id?.phone_number ||
          app.phone ||
          "Not specified";
        const location =
          resume.current_location ||
          resume.preferred_location ||
          app.preferred_location ||
          "Not specified";
        const resumeUrl =
          resume.resume_pdf_url ||
          resume.resume_pdf ||
          app.talent_resume_latest_url ||
          null;
        return {
          id: app.id,
          talent_id: app.talent,
          name,
          email,
          phone,
          location,
          position: app.job_posting_details?.title || "Not specified",
          experience: resume.summary || "Not specified",
          skills: parseJsonArray(resume.skills),
          match: app.score ?? 0,
          status: app.status_display || app.status,
          appliedDate: new Date(app.application_date).toLocaleDateString(),
          resumeUrl,
          portfolioUrl: resume.portfolio_url || "",
          expectedSalary: resume.expected_salary ?? "Not specified",
          availability: resume.availability ?? "Not specified",
          languages: resume.languages ?? [],
          education: resume.degree_name
            ? `${resume.degree_name} (${resume.degree_institution_name || ""})`
            : "Not specified",
          certifications: parseJsonArray(resume.certifications),
          previousCompanies: resume.current_company
            ? [resume.current_company]
            : [],
        };
      } catch (err) {
        handleError(err, "load application details");
        return null;
      } finally {
        setLoading((prev) => ({ ...prev, details: false }));
      }
    },
    [handleError],
  );

  const getStatusColor = (status) => {
    const s = status?.toLowerCase();
    if (s === "scheduled" || s === "hired") return "success";
    if (s === "reviewed") return "warning";
    if (s === "applied") return "info";
    if (s === "rejected") return "error";
    if (s === "shortlisted") return "primary";
    return "default";
  };

  const getMatchColor = (match) => {
    if (match < 0) return "error";
    if (match >= 90) return "success";
    if (match >= 75) return "warning";
    return "info";
  };

  const handlePostJob = async (jobId) => {
    setLoading((prev) => ({ ...prev, postJob: true }));
    try {
      await apiClient.patch(`/job-postings/${jobId}/`, { status: "PUBLISHED" });
      setJobPosts((prev) =>
        prev.map((job) =>
          job.id === jobId ? { ...job, status: "published" } : job,
        ),
      );
      showNotification("Job posted successfully!", "success");
    } catch (err) {
      handleError(err, "post job");
    } finally {
      setLoading((prev) => ({ ...prev, postJob: false }));
    }
  };

  const handleDeleteJob = async (jobId) => {
    setLoading((prev) => ({ ...prev, deleteJob: true }));
    try {
      await apiClient.delete(`/job-postings/${jobId}/`);
      setJobPosts((prev) => prev.filter((job) => job.id !== jobId));
      showNotification("Job deleted successfully!", "success");
    } catch (err) {
      handleError(err, "delete job");
    } finally {
      setLoading((prev) => ({ ...prev, deleteJob: false }));
    }
  };

  const handleDeleteCurrentJob = async () => {
    if (!selectedJobPost) return;
    if (
      window.confirm(
        "Are you sure you want to delete this job post? This action cannot be undone.",
      )
    ) {
      await handleDeleteJob(selectedJobPost.id);
      setSelectedJobPost(null);
    }
  };

  const handleViewDraftJob = (job) => {
    setSelectedDraftJob(job);
    setEditDraftData(job);
    setEditMode(false);
    setOpenDraftDialog(true);
  };

  const handleEditDraftJob = () => {
    setEditMode(true);
    setEditDraftData(selectedDraftJob);
  };

  const handleDraftFieldChange = (field, value) => {
    setEditDraftData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSaveDraft = async () => {
    setSavingDraft(true);
    try {
      await apiClient.patch(
        `/job-postings/${editDraftData.id}/`,
        editDraftData,
      );
      setJobPosts((prev) =>
        prev.map((job) =>
          job.id === editDraftData.id ? { ...job, ...editDraftData } : job,
        ),
      );
      setSelectedDraftJob(editDraftData);
      setEditMode(false);
      showNotification("Draft job updated successfully!", "success");
    } catch (err) {
      handleError(err, "update draft job");
    } finally {
      setSavingDraft(false);
    }
  };

  const currentCandidates = selectedJobPost?.applications || [];
  const filteredCandidates = currentCandidates.filter((candidate) => {
    if (filterStatus === "all") return true;
    return candidate?.status?.toLowerCase() === filterStatus.toLowerCase();
  });

  const handleStatusChange = async (candidateId, newStatus) => {
    if (!selectedJobPost) return;

    const originalApplications = [...currentCandidates];
    const originalSummaryStats = { ...selectedJobPost.summary_stats };

    const updatedApplications = currentCandidates.map((app) =>
      app.id === candidateId ? { ...app, status: newStatus } : app,
    );
    const updatedSummaryStats = {
      ...originalSummaryStats,
      under_review_count: updatedApplications.filter(
        (app) => app.status?.toLowerCase() === "reviewed",
      ).length,
      interviews_scheduled_count: updatedApplications.filter(
        (app) => app.status?.toLowerCase() === "scheduled",
      ).length,
      shortlisted_count: updatedApplications.filter(
        (app) => app.status?.toLowerCase() === "shortlisted",
      ).length,
    };
    setSelectedJobPost((prev) => ({
      ...prev,
      applications: updatedApplications,
      summary_stats: updatedSummaryStats,
    }));
    if (selectedCandidate && selectedCandidate.id === candidateId) {
      setSelectedCandidate((prev) => ({ ...prev, status: newStatus }));
    }

    try {
      await apiClient.post(`/applications/${candidateId}/update-status/`, {
        status: newStatus,
      });
      showNotification(`Candidate status updated to ${newStatus}`, "success");
    } catch (err) {
      handleError(err, "update candidate status");
      setSelectedJobPost((prev) => ({
        ...prev,
        applications: originalApplications,
        summary_stats: originalSummaryStats,
      }));
      if (selectedCandidate && selectedCandidate.id === candidateId) {
        const originalStatus = originalApplications.find(
          (app) => app.id === candidateId,
        )?.status;
        setSelectedCandidate((prev) => ({ ...prev, status: originalStatus }));
      }
    }
  };

  const handleInterviewSubmit = async () => {
    if (!interviewData.date || !interviewData.time) {
      showNotification(
        "Please select a date and time for the interview.",
        "warning",
      );
      return;
    }
    if (interviewData.type === "in-person" && !interviewData.address.trim()) {
      showNotification(
        "Please enter an address for the in-person interview.",
        "warning",
      );
      return;
    }
    try {
      const scheduledAtISO = new Date(
        `${interviewData.date}T${interviewData.time}`,
      ).toISOString();
      let interviewLocation = interviewData.type;
      if (interviewData.type === "video") {
        interviewLocation = interviewData.link.trim() || "Video Call";
      } else if (interviewData.type === "in-person") {
        let locationString = interviewData.address.trim();
        if (interviewData.mapUrl.trim()) {
          locationString += ` | Map: ${interviewData.mapUrl.trim()}`;
        }
        interviewLocation = locationString;
      }
      interviewLocation = interviewLocation.slice(0, 255);
      const payload = {
        application: selectedCandidate.id,
        scheduled_at: scheduledAtISO,
        location: interviewLocation,
        interview_status: "Scheduled",
      };
      await apiClient.post("/interviews/", payload);
      await handleStatusChange(selectedCandidate.id, "Scheduled");
      showNotification("Interview scheduled successfully!", "success");
      setOpenDialog(false);
      setInterviewData({
        date: "",
        time: "",
        type: "video",
        notes: "",
        link: "",
        address: "",
        mapUrl: "",
      });
    } catch (err) {
      handleError(err, "schedule interview");
    }
  };

  const handleViewCandidate = async (candidate) => {
    const statusToSet =
      candidate.status?.toLowerCase() === "applied" ? "REVIEWED" : null;

    setTabValue(0);
    setSelectedCandidate(candidate);
    setOpenDialog(true);

    if (statusToSet) {
      await handleStatusChange(candidate.id, statusToSet);
    }
    const fullDetails = await fetchApplicationDetails(
      selectedJobPost.id,
      candidate.id,
    );
    if (fullDetails) {
      setSelectedCandidate(fullDetails);
    }
  };

  const handleScheduleInterview = (candidate) => {
    setSelectedCandidate(candidate);
    setTabValue(1);
    setOpenDialog(true);
  };

  const handleShortlistCandidate = () => {
    if (selectedCandidate) {
      handleStatusChange(selectedCandidate.id, "SHORTLISTED");
    }
  };

  const renderDraftJobDialog = () => {
    const ensureArray = (value) => {
      if (Array.isArray(value)) {
        return value;
      }
      if (typeof value === "string" && value.trim()) {
        return value.split(",").map((s) => s.trim());
      }
      return [];
    };

    return (
      <Dialog
        open={openDraftDialog}
        onClose={() => {
          setOpenDraftDialog(false);
          setEditMode(false);
        }}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 4 } }}
      >
        {selectedDraftJob && (
          <>
            <DialogTitle sx={{ pb: 2 }}>
              <Typography variant="h5" component="div" sx={{ fontWeight: 600 }}>
                {editMode ? "Edit Draft Job" : "Draft Job Details"}
              </Typography>
            </DialogTitle>
            <DialogContent dividers sx={{ p: 3 }}>
              {editMode ? (
                <Stack spacing={3}>
                  <TextField
                    label="Title"
                    fullWidth
                    value={editDraftData.title || ""}
                    onChange={(e) =>
                      handleDraftFieldChange("title", e.target.value)
                    }
                    variant="outlined"
                  />
                  <TextField
                    label="Description"
                    fullWidth
                    multiline
                    minRows={4}
                    value={editDraftData.description || ""}
                    onChange={(e) =>
                      handleDraftFieldChange("description", e.target.value)
                    }
                    variant="outlined"
                  />
                  <TextField
                    label="Location"
                    fullWidth
                    value={editDraftData.location || ""}
                    onChange={(e) =>
                      handleDraftFieldChange("location", e.target.value)
                    }
                    variant="outlined"
                  />
                  <Stack direction="row" spacing={2}>
                    <TextField
                      label="Minimum Salary"
                      type="number"
                      fullWidth
                      value={editDraftData.salary_min || ""}
                      onChange={(e) =>
                        handleDraftFieldChange("salary_min", e.target.value)
                      }
                      variant="outlined"
                    />
                    <TextField
                      label="Maximum Salary"
                      type="number"
                      fullWidth
                      value={editDraftData.salary_max || ""}
                      onChange={(e) =>
                        handleDraftFieldChange("salary_max", e.target.value)
                      }
                      variant="outlined"
                    />
                    <FormControl sx={{ minWidth: 120 }}>
                      <InputLabel>Currency</InputLabel>
                      <Select
                        value={editDraftData.salary_currency || "USD"}
                        onChange={(e) =>
                          handleDraftFieldChange(
                            "salary_currency",
                            e.target.value,
                          )
                        }
                        label="Currency"
                      >
                        <MenuItem value="USD">USD</MenuItem>
                        <MenuItem value="EUR">EUR</MenuItem>
                        <MenuItem value="GBP">GBP</MenuItem>
                        <MenuItem value="INR">INR</MenuItem>
                      </Select>
                    </FormControl>
                  </Stack>
                  <FormControl fullWidth>
                    <InputLabel>Job Type</InputLabel>
                    <Select
                      value={editDraftData.job_type || ""}
                      onChange={(e) =>
                        handleDraftFieldChange("job_type", e.target.value)
                      }
                      label="Job Type"
                    >
                      <MenuItem value="Full-time">Full-time</MenuItem>
                      <MenuItem value="Part-time">Part-time</MenuItem>
                      <MenuItem value="Contract">Contract</MenuItem>
                      <MenuItem value="Internship">Internship</MenuItem>
                      <MenuItem value="Temporary">Temporary</MenuItem>
                    </Select>
                  </FormControl>
                  <TextField
                    label="Requirements"
                    fullWidth
                    helperText="Enter comma-separated values"
                    value={
                      Array.isArray(editDraftData.requirements)
                        ? editDraftData.requirements.join(", ")
                        : editDraftData.requirements || ""
                    }
                    onChange={(e) =>
                      handleDraftFieldChange("requirements", e.target.value)
                    }
                    variant="outlined"
                  />
                  <TextField
                    label="Benefits"
                    fullWidth
                    helperText="Enter comma-separated values"
                    value={
                      Array.isArray(editDraftData.benefits)
                        ? editDraftData.benefits.join(", ")
                        : editDraftData.benefits || ""
                    }
                    onChange={(e) =>
                      handleDraftFieldChange("benefits", e.target.value)
                    }
                    variant="outlined"
                  />
                  <TextField
                    label="Required Skills"
                    fullWidth
                    helperText="Enter comma-separated values"
                    value={
                      Array.isArray(editDraftData.required_skills)
                        ? editDraftData.required_skills.join(", ")
                        : editDraftData.required_skills || ""
                    }
                    onChange={(e) =>
                      handleDraftFieldChange("required_skills", e.target.value)
                    }
                    variant="outlined"
                  />
                  <Stack direction="row" spacing={2}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={!!editDraftData.remote_work}
                          onChange={(e) =>
                            handleDraftFieldChange(
                              "remote_work",
                              e.target.checked,
                            )
                          }
                        />
                      }
                      label="Remote Work"
                    />
                    <FormControlLabel
                      control={
                        <Switch
                          checked={!!editDraftData.visa_sponsorship}
                          onChange={(e) =>
                            handleDraftFieldChange(
                              "visa_sponsorship",
                              e.target.checked,
                            )
                          }
                        />
                      }
                      label="Visa Sponsorship"
                    />
                  </Stack>
                </Stack>
              ) : (
                <Stack spacing={2.5}>
                  <Box>
                    <Typography
                      variant="subtitle2"
                      color="text.secondary"
                      gutterBottom
                    >
                      Title
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{ wordBreak: "break-word" }}
                    >
                      {selectedDraftJob.title}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography
                      variant="subtitle2"
                      color="text.secondary"
                      gutterBottom
                    >
                      Description
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{ wordBreak: "break-word" }}
                    >
                      {selectedDraftJob.description ||
                        "No description provided."}
                    </Typography>
                  </Box>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Typography
                        variant="subtitle2"
                        color="text.secondary"
                        gutterBottom
                      >
                        Location
                      </Typography>
                      <Typography
                        variant="body1"
                        sx={{ wordBreak: "break-word" }}
                      >
                        {selectedDraftJob.location || "Not specified"}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography
                        variant="subtitle2"
                        color="text.secondary"
                        gutterBottom
                      >
                        Job Type
                      </Typography>
                      <Typography
                        variant="body1"
                        sx={{ wordBreak: "break-word" }}
                      >
                        {selectedDraftJob.job_type_display ||
                          selectedDraftJob.job_type ||
                          "Not specified"}
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography
                        variant="subtitle2"
                        color="text.secondary"
                        gutterBottom
                      >
                        Salary
                      </Typography>
                      <Typography
                        variant="body1"
                        sx={{ wordBreak: "break-word" }}
                      >
                        {selectedDraftJob.salary_min &&
                        selectedDraftJob.salary_max
                          ? `${new Intl.NumberFormat().format(
                              selectedDraftJob.salary_min,
                            )} - ${new Intl.NumberFormat().format(
                              selectedDraftJob.salary_max,
                            )} ${selectedDraftJob.salary_currency || ""}`
                          : "Not specified"}
                      </Typography>
                    </Grid>
                  </Grid>
                  <Box>
                    <Typography
                      variant="subtitle2"
                      color="text.secondary"
                      gutterBottom
                    >
                      Required Skills
                    </Typography>
                    <Stack
                      direction="row"
                      flexWrap="wrap"
                      spacing={1}
                      useFlexGap
                    >
                      {ensureArray(selectedDraftJob.required_skills).length >
                      0 ? (
                        ensureArray(selectedDraftJob.required_skills).map(
                          (skill, i) => (
                            <Chip key={i} label={skill} size="small" />
                          ),
                        )
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          Not specified
                        </Typography>
                      )}
                    </Stack>
                  </Box>
                  <Box>
                    <Typography
                      variant="subtitle2"
                      color="text.secondary"
                      gutterBottom
                    >
                      Requirements
                    </Typography>
                    <Typography
                      variant="body1"
                      component="div"
                      sx={{ wordBreak: "break-word" }}
                    >
                      {ensureArray(selectedDraftJob.requirements).length > 0 ? (
                        <ul style={{ margin: 0, paddingLeft: "20px" }}>
                          {ensureArray(selectedDraftJob.requirements).map(
                            (req, i) => (
                              <li key={i}>{req}</li>
                            ),
                          )}
                        </ul>
                      ) : (
                        "Not specified"
                      )}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography
                      variant="subtitle2"
                      color="text.secondary"
                      gutterBottom
                    >
                      Benefits
                    </Typography>
                    <Stack
                      direction="row"
                      flexWrap="wrap"
                      spacing={1}
                      useFlexGap
                    >
                      {ensureArray(selectedDraftJob.benefits).length > 0 ? (
                        ensureArray(selectedDraftJob.benefits).map(
                          (benefit, i) => (
                            <Chip key={i} label={benefit} size="small" />
                          ),
                        )
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          Not specified
                        </Typography>
                      )}
                    </Stack>
                  </Box>
                  <Stack direction="row" spacing={4}>
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">
                        Remote Work
                      </Typography>
                      <Chip
                        label={
                          selectedDraftJob.remote_work
                            ? "Available"
                            : "Not Available"
                        }
                        color={
                          selectedDraftJob.remote_work ? "success" : "default"
                        }
                        size="small"
                        variant="outlined"
                      />
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">
                        Visa Sponsorship
                      </Typography>
                      <Chip
                        label={
                          selectedDraftJob.visa_sponsorship
                            ? "Available"
                            : "Not Available"
                        }
                        color={
                          selectedDraftJob.visa_sponsorship
                            ? "success"
                            : "default"
                        }
                        size="small"
                        variant="outlined"
                      />
                    </Box>
                  </Stack>
                  <Box>
                    <Typography
                      variant="subtitle2"
                      color="text.secondary"
                      gutterBottom
                    >
                      Created At
                    </Typography>
                    <Typography variant="body1">
                      {selectedDraftJob.created_at
                        ? new Date(selectedDraftJob.created_at).toLocaleString()
                        : "N/A"}
                    </Typography>
                  </Box>
                </Stack>
              )}
            </DialogContent>
            <DialogActions sx={{ p: 2, gap: 1 }}>
              <Button
                onClick={() => {
                  if (editMode) setEditMode(false);
                  else setOpenDraftDialog(false);
                }}
                variant="outlined"
                sx={{ borderRadius: 2 }}
              >
                {editMode ? "Cancel" : "Close"}
              </Button>
              {editMode ? (
                <Button
                  onClick={handleSaveDraft}
                  variant="contained"
                  disabled={savingDraft}
                  startIcon={
                    savingDraft ? <CircularProgress size={16} /> : null
                  }
                  sx={{ borderRadius: 2 }}
                >
                  {savingDraft ? "Saving..." : "Save"}
                </Button>
              ) : (
                <Button
                  onClick={handleEditDraftJob}
                  variant="contained"
                  startIcon={<EditIcon />}
                  sx={{ borderRadius: 2 }}
                >
                  Edit
                </Button>
              )}
            </DialogActions>
          </>
        )}
      </Dialog>
    );
  };

  const renderJobPostListView = () => {
    const postedJobs = jobPosts.filter(
      (job) => job.status?.toLowerCase() === "published",
    );
    const draftJobs = jobPosts.filter(
      (job) => job.status?.toLowerCase() === "draft",
    );
    const postedCount = postedJobs.length;
    const draftCount = draftJobs.length;
    const totalApplications = jobPosts.reduce(
      (sum, job) => sum + (job.applications_count || 0),
      0,
    );

    const renderJobCards = (jobs, isDraft = false, page, perPage) => {
      const pageCount = Math.ceil(jobs.length / perPage);
      const paginatedJobs = jobs.slice((page - 1) * perPage, page * perPage);

      if (loading.jobs) {
        return (
          <Grid container spacing={{ xs: 2, md: 3 }}>
            {Array.from(new Array(8)).map((_, index) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={index}>
                {isDraft ? (
                  <Card
                    sx={{
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      borderRadius: 3,
                      border: "1px dashed",
                      borderColor: "grey.300",
                      backgroundColor: theme.palette.mint?.main,
                    }}
                  >
                    <CardContent sx={{ flexGrow: 1, p: 2 }}>
                      <Stack spacing={1.5} direction="column" height="100%">
                        <Box>
                          <Skeleton variant="text" width="60%" />
                          <Skeleton variant="text" height={40} />
                          <Skeleton variant="text" width="40%" />
                        </Box>
                        <Box flexGrow={1} />
                      </Stack>
                    </CardContent>
                    <Box sx={{ p: 2, pt: 0 }}>
                      <Stack spacing={1}>
                        <Skeleton
                          variant="rectangular"
                          height={36}
                          sx={{ borderRadius: 2 }}
                        />
                        <Stack direction="row" spacing={1}>
                          <Skeleton
                            variant="rectangular"
                            height={36}
                            sx={{ borderRadius: 2, width: "100%" }}
                          />
                          <Skeleton
                            variant="rectangular"
                            height={36}
                            sx={{ borderRadius: 2, width: "100%" }}
                          />
                        </Stack>
                      </Stack>
                    </Box>
                  </Card>
                ) : (
                  <Card
                    sx={{
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      borderRadius: 3,
                      boxShadow: "0 8px 20px rgba(0,0,0,0.12)",
                      bgcolor: "grey.200",
                    }}
                  >
                    <CardContent
                      sx={{
                        flexGrow: 1,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        p: 1,
                      }}
                    >
                      <Stack direction="column" alignItems="center" spacing={2}>
                        <Skeleton
                          variant="rectangular"
                          width={100}
                          height={50}
                        />
                        <Skeleton variant="text" width={80} />
                        <Divider sx={{ my: 1, width: "80px" }} />
                        <Skeleton variant="text" width={150} height={30} />
                        <Skeleton variant="text" width={100} />
                      </Stack>
                    </CardContent>
                    <Box sx={{ p: 1 }}>
                      <Skeleton
                        variant="rectangular"
                        height={42}
                        sx={{ borderRadius: 1 }}
                      />
                    </Box>
                  </Card>
                )}
              </Grid>
            ))}
          </Grid>
        );
      }

      if (jobs.length === 0) {
        return (
          <Paper
            variant="outlined"
            sx={{
              p: 4,
              textAlign: "center",
              borderRadius: 3,
              bgcolor:
                theme.palette.mode === "light" ? "grey.50" : "transparent",
              borderStyle: "dashed",
            }}
          >
            <Typography variant="h6" color="text.secondary">
              {isDraft ? "No draft jobs found." : "No active jobs posted."}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {isDraft
                ? "Create a new job to save it as a draft."
                : "New jobs you post will appear here."}
            </Typography>
          </Paper>
        );
      }

      return (
        <Stack spacing={3} alignItems="center">
          {isDraft ? (
            <Grid container spacing={{ xs: 2, md: 3 }}>
              {paginatedJobs.map((job) => (
                <Grid item xs={12} sm={6} md={4} lg={3} key={job.id}>
                  <Card
                    sx={{
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      borderRadius: 3,
                      transition: "all 0.3s ease",
                      border: "1px dashed",
                      borderColor: theme.palette.warning.main,
                      bgcolor:
                        theme.palette.mode === "light"
                          ? theme.palette.mint?.main
                          : theme.palette.mint?.main,
                      "&:hover": {
                        transform: "translateY(-4px)",
                        boxShadow: theme.shadows[8],
                        borderColor: theme.palette.warning.dark,
                      },
                    }}
                  >
                    <CardContent sx={{ flexGrow: 1, p: 2 }}>
                      <Stack spacing={1.5} direction="column" height="100%">
                        <Box>
                          <Stack
                            direction="row"
                            justifyContent="space-between"
                            alignItems="center"
                            mb={1}
                          >
                            <Typography
                              variant="caption"
                              sx={{
                                fontWeight: 700,
                                textTransform: "uppercase",
                                color: theme.palette.warning.main,
                                letterSpacing: 0.8,
                              }}
                            >
                              Position
                            </Typography>
                            <Chip
                              label="Draft"
                              color="warning"
                              size="small"
                              sx={{
                                fontWeight: 600,
                                fontSize: "0.7rem",
                                height: 20,
                              }}
                            />
                          </Stack>
                          <Typography
                            variant="h6"
                            component="p"
                            sx={{
                              fontWeight: 600,
                              lineHeight: 1.3,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              display: "-webkit-box",
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: "vertical",
                              minHeight: "2.6em",
                            }}
                          >
                            {job.title}
                          </Typography>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            noWrap
                          >
                            {job.location}
                          </Typography>
                        </Box>
                        <Box flexGrow={1} />
                      </Stack>
                    </CardContent>
                    <Box sx={{ p: 2, pt: 0 }}>
                      <Stack spacing={1}>
                        <Button
                          variant="contained"
                          color="success"
                          fullWidth
                          startIcon={<PublishIcon />}
                          disabled={loading.postJob}
                          onClick={() => handlePostJob(job.id)}
                          sx={{
                            borderRadius: 2,
                            textTransform: "none",
                            fontWeight: 600,
                          }}
                        >
                          {loading.postJob ? (
                            <CircularProgress size={20} color="inherit" />
                          ) : (
                            "Publish"
                          )}
                        </Button>
                        <Stack direction="row" spacing={1}>
                          <Button
                            variant="outlined"
                            color="primary"
                            fullWidth
                            startIcon={<Visibility />}
                            onClick={() => handleViewDraftJob(job)}
                            sx={{
                              borderRadius: 2,
                              textTransform: "none",
                              fontWeight: 600,
                            }}
                          >
                            View
                          </Button>
                          <Button
                            variant="outlined"
                            color="error"
                            fullWidth
                            startIcon={<DeleteIcon />}
                            disabled={loading.deleteJob}
                            onClick={() => handleDeleteJob(job.id)}
                            sx={{
                              borderRadius: 2,
                              textTransform: "none",
                              fontWeight: 600,
                            }}
                          >
                            Delete
                          </Button>
                        </Stack>
                      </Stack>
                    </Box>
                  </Card>
                </Grid>
              ))}
            </Grid>
          ) : (
            <Box
              sx={{
                width: "100%",
                display: "grid",
                gap: 2,
                gridTemplateColumns: {
                  xs: "repeat(1, 1fr)",
                  sm: "repeat(2, 1fr)",
                  md: "repeat(3, 1fr)",
                  lg: "repeat(4, 1fr)",
                },
              }}
            >
              {paginatedJobs.map((job) => (
                <Card
                  key={job.id}
                  sx={{
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    borderRadius: 4,
                    boxShadow:
                      theme.palette.mode === "dark"
                        ? "0 8px 32px rgba(0,0,0,0.4)"
                        : "0 8px 32px rgba(0,0,0,0.08)",
                    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                    position: "relative",
                    overflow: "hidden",
                    border: `1px solid ${theme.palette.divider}`,
                    backgroundColor: theme.palette.background.paper,
                    backgroundImage:
                      theme.palette.mode === "dark"
                        ? `linear-gradient(to bottom, rgba(26, 26, 26, 0.85), rgba(26, 26, 26, 0.95)), url(${process.env.PUBLIC_URL}/assets/card5.jpg)`
                        : `linear-gradient(to bottom, rgba(255, 255, 255, 0.7), rgba(255, 255, 255, 0.8)), url(${process.env.PUBLIC_URL}/assets/card5.jpg)`,
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                    "&:hover": {
                      transform: "translateY(-8px)",
                      boxShadow:
                        theme.palette.mode === "dark"
                          ? "0 12px 40px rgba(0,0,0,0.5)"
                          : "0 12px 40px rgba(0,0,0,0.15)",
                      borderColor: theme.palette.primary.main,
                    },
                  }}
                >
                  <CardContent
                    sx={{
                      flexGrow: 1,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      p: 1,
                    }}
                  >
                    <Stack
                      direction="column"
                      alignItems="center"
                      spacing={2}
                      sx={{
                        color: theme.palette.text.primary,
                        textAlign: "center",
                      }}
                    >
                      <Typography
                        sx={{
                          fontWeight: 800,
                          fontSize: "3.5rem",
                          lineHeight: 1,
                          color: theme.palette.primary.main,
                          textShadow:
                            theme.palette.mode === "dark"
                              ? "0 2px 10px rgba(212, 175, 55, 0.2)"
                              : "none",
                        }}
                      >
                        {job.applications_count || 0}
                      </Typography>
                      <Typography
                        sx={{
                          fontWeight: 500,
                          letterSpacing: "1px",
                          fontSize: "0.8rem",
                          color: theme.palette.text.secondary,
                        }}
                      >
                        {job.applications_count === 1
                          ? "Application"
                          : "Applications"}
                      </Typography>
                      <Divider
                        sx={{
                          my: 1.5,
                          width: "60px",
                          height: "3px",
                          borderRadius: "2px",
                          backgroundColor: theme.palette.primary.main,
                          opacity: 0.6,
                          border: "none",
                        }}
                      />
                      <Typography
                        sx={{
                          fontWeight: "bold",
                          fontSize: "1.25rem",
                          letterSpacing: "1.5px",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                        }}
                      >
                        {job.title}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "0.8rem",
                          letterSpacing: "1px",
                          textTransform: "uppercase",
                          opacity: 0.9,
                          color: theme.palette.text.secondary,
                        }}
                      >
                        {job.department}
                      </Typography>
                    </Stack>
                  </CardContent>
                  <Box sx={{ p: 1, display: "flex", justifyContent: "center" }}>
                    <StyledMuiButton
                      onClick={() => handleSelectJobPost(job)}
                      disabled={
                        loading.candidates && selectedJobPost?.id === job.id
                      }
                      endIcon={
                        !(
                          loading.candidates && selectedJobPost?.id === job.id
                        ) && <CustomDotIcon />
                      }
                    >
                      {loading.candidates && selectedJobPost?.id === job.id ? (
                        <CircularProgress
                          size={24}
                          sx={{ color: theme.palette.primary.main }}
                        />
                      ) : (
                        "Manage"
                      )}
                    </StyledMuiButton>
                  </Box>
                </Card>
              ))}
            </Box>
          )}

          {pageCount > 1 && (
            <Pagination
              count={pageCount}
              page={page}
              onChange={(e, value) => {
                isDraft ? setDraftJobsPage(value) : setPostedJobsPage(value);
              }}
              color={isDraft ? "warning" : "primary"}
              sx={{ pt: 2 }}
            />
          )}
        </Stack>
      );
    };

    return (
      <Box sx={{ p: { xs: 2, sm: 3 } }}>
        <Paper
          elevation={4}
          sx={{
            p: { xs: 2, md: 3 },
            mb: 4,
            borderRadius: 4,
            backgroundColor: theme.palette.mint?.main,
            color: theme.palette.primary?.main,
          }}
        >
          <Grid
            container
            spacing={{ xs: 2, md: 0 }}
            alignItems="center"
            justifyContent="space-around"
          >
            <Grid item xs={12} sm={4}>
              <Box textAlign="center">
                <Typography variant="h3" sx={{ fontWeight: 700 }}>
                  {loading.jobs ? (
                    <Skeleton
                      width={60}
                      sx={{ mx: "auto", bgcolor: "rgba(255,255,255,0.3)" }}
                    />
                  ) : (
                    postedCount
                  )}
                </Typography>
                <Typography variant="body1" sx={{ opacity: 0.9 }}>
                  Posted Jobs
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Box textAlign="center" sx={{ my: { xs: 2, sm: 0 } }}>
                <Typography variant="h3" sx={{ fontWeight: 700 }}>
                  {loading.jobs ? (
                    <Skeleton
                      width={60}
                      sx={{ mx: "auto", bgcolor: "rgba(255,255,255,0.3)" }}
                    />
                  ) : (
                    draftCount
                  )}
                </Typography>
                <Typography variant="body1" sx={{ opacity: 0.9 }}>
                  Draft Jobs
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Box textAlign="center">
                <Typography variant="h3" sx={{ fontWeight: 700 }}>
                  {loading.jobs ? (
                    <Skeleton
                      width={60}
                      sx={{ mx: "auto", bgcolor: "rgba(255,255,255,0.3)" }}
                    />
                  ) : (
                    totalApplications
                  )}
                </Typography>
                <Typography variant="body1" sx={{ opacity: 0.9 }}>
                  Total Applications
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Paper>
        <Paper
          elevation={0}
          variant="outlined"
          sx={{
            borderRadius: 3,
            overflow: "hidden",
            backgroundColor: theme.palette.mint?.main,
          }}
        >
          <Box
            sx={{
              borderBottom: 1,
              borderColor: "divider",
              display: "flex",
              justifyContent: "center",
            }}
          >
            <Tabs
              value={jobTabValue}
              onChange={(e, newValue) => setJobTabValue(newValue)}
              sx={{
                "& .MuiTab-root": {
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: "1rem",
                  minHeight: 60,
                  opacity: 0.7,
                },
                "& .Mui-selected": {
                  opacity: 1,
                },
              }}
            >
              <Tab
                label={
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <Typography>Posted Jobs</Typography>
                    <Chip
                      label={loading.jobs ? "..." : postedCount}
                      size="small"
                      color="primary"
                      sx={{ fontWeight: 600 }}
                    />
                  </Stack>
                }
                sx={{
                  "&.Mui-selected": {
                    color: "primary.main",
                  },
                }}
              />
              <Tab
                label={
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <Typography>Draft Jobs</Typography>
                    <Chip
                      label={loading.jobs ? "..." : draftCount}
                      size="small"
                      color="warning"
                      sx={{ fontWeight: 600 }}
                    />
                  </Stack>
                }
                sx={{
                  "&.Mui-selected": {
                    color: "warning.main",
                  },
                }}
              />
            </Tabs>
          </Box>
          <Box sx={{ p: { xs: 2, sm: 3 } }}>
            <TabPanel value={jobTabValue} index={0}>
              {renderJobCards(postedJobs, false, postedJobsPage, JOBS_PER_PAGE)}
            </TabPanel>
            <TabPanel value={jobTabValue} index={1}>
              {renderJobCards(draftJobs, true, draftJobsPage, JOBS_PER_PAGE)}
            </TabPanel>
          </Box>
        </Paper>
      </Box>
    );
  };

  const renderCandidateListView = () => {
    const avgMatch = selectedJobPost?.summary_stats?.average_match;
    const avgMatchValue =
      avgMatch === "N/A" || avgMatch == null ? "N/A" : `${avgMatch}%`;

    const statsData = [
      {
        label: "Total Applications",
        value: selectedJobPost.summary_stats?.total_applications ?? 0,
      },
      {
        label: "Shortlisted",
        value: selectedJobPost.summary_stats?.shortlisted_count ?? 0,
      },
      {
        label: "In Review",
        value: selectedJobPost.summary_stats?.under_review_count ?? 0,
      },
      {
        label: "Interviews Scheduled",
        value: selectedJobPost.summary_stats?.interviews_scheduled_count ?? 0,
      },
      {
        label: "Average Match",
        value: avgMatchValue,
      },
    ];

    const candidatesPageCount = Math.ceil(
      filteredCandidates.length / CANDIDATES_PER_PAGE,
    );
    const paginatedCandidates = filteredCandidates.slice(
      (candidatesPage - 1) * CANDIDATES_PER_PAGE,
      candidatesPage * CANDIDATES_PER_PAGE,
    );

    const potentialCandidatesPageCount = Math.ceil(
      potentialCandidates.length / POTENTIAL_CANDIDATES_PER_PAGE,
    );
    const paginatedPotentialCandidates = potentialCandidates.slice(
      (potentialCandidatesPage - 1) * POTENTIAL_CANDIDATES_PER_PAGE,
      potentialCandidatesPage * POTENTIAL_CANDIDATES_PER_PAGE,
    );

    return (
      <Box sx={{ p: { xs: 2, sm: 3 } }}>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => {
            setSelectedJobPost(null);
            setPotentialCandidates([]);
          }}
          sx={{
            mb: 1,
            textTransform: "none",
            fontWeight: 600,
            backgroundColor: theme.palette.mint?.main,
          }}
        >
          Back to Jobs
        </Button>
        <Paper
          elevation={0}
          variant="outlined"
          sx={{
            p: { xs: 2, md: 3 },
            mb: 3,
            borderRadius: 3,
            backgroundColor: theme.palette.mint?.main,
          }}
        >
          <Stack
            direction={{ xs: "column", md: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "flex-start", md: "center" }}
            spacing={2}
          >
            <Box>
              <Typography
                variant="h4"
                sx={{ fontWeight: 700, wordBreak: "break-word" }}
              >
                {selectedJobPost?.title}
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Manage candidates for this role.
              </Typography>
            </Box>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={1.5}
              sx={{ width: { xs: "100%", md: "auto" } }}
            >
              <FormControl fullWidth sx={{ minWidth: 200 }}>
                <InputLabel>Filter Status</InputLabel>
                <Select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  label="Filter Status"
                  sx={{ borderRadius: 2 }}
                >
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="Applied">Applied</MenuItem>
                  <MenuItem value="Shortlisted">Shortlisted</MenuItem>
                  <MenuItem value="reviewed">reviewed</MenuItem>
                  <MenuItem value="Interview Scheduled">
                    Interview Scheduled
                  </MenuItem>
                  <MenuItem value="Hired">Hired</MenuItem>
                  <MenuItem value="Rejected">Rejected</MenuItem>
                </Select>
              </FormControl>
              <Button
                variant="outlined"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={handleDeleteCurrentJob}
                disabled={loading.deleteJob}
                sx={{
                  textTransform: "none",
                  fontWeight: 600,
                  borderRadius: 2,
                }}
              >
                {loading.deleteJob ? (
                  <CircularProgress size={20} />
                ) : (
                  "Delete Job"
                )}
              </Button>
            </Stack>
          </Stack>
        </Paper>
        <>
          <Box
            sx={{
              mb: 3,
              display: "grid",
              gap: { xs: 2, md: 3 },
              gridTemplateColumns: {
                xs: "repeat(1, 1fr)",
                sm: "repeat(2, 1fr)",
                md: "repeat(3, 1fr)",
                lg: "repeat(5, 1fr)",
              },
            }}
          >
            {statsData.map((stat) => (
              <Card
                key={stat.label}
                sx={{
                  position: "relative",
                  borderRadius: "16px",
                  boxShadow: "0 8px 16px rgba(0,0,0,0.08)",
                  overflow: "hidden",
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  bgcolor: theme.palette.primary?.main,
                  color:
                    theme.palette.primary?.contrastText ||
                    theme.palette.getContrastText(
                      theme.palette.primary?.main || "#000",
                    ),
                  minHeight: "150px",
                }}
              >
                <Box
                  sx={{
                    position: "absolute",
                    top: 0,
                    right: 0,
                    bottom: 0,
                    width: "25%",
                    bgcolor: theme.palette.accent?.main,
                    "&::before": {
                      content: '""',
                      position: "absolute",
                      top: 0,
                      left: 0,
                      bottom: 0,
                      width: "30px",
                      transform: "translateX(-50%)",
                      bgcolor: theme.palette.primary?.main,
                      borderTopRightRadius: "100%",
                      borderBottomRightRadius: "100%",
                    },
                  }}
                />
                <CardContent
                  sx={{
                    position: "relative",
                    zIndex: 1,
                    flexGrow: 1,
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "space-between",
                    p: 3,
                  }}
                >
                  <Typography
                    sx={{
                      fontWeight: 600,
                      fontSize: "0.8rem",
                      textTransform: "uppercase",
                      opacity: 0.9,
                    }}
                  >
                    {stat.label}
                  </Typography>
                  <Typography
                    variant="h3"
                    sx={{
                      fontWeight: 700,
                      fontSize: "2.75rem",
                    }}
                  >
                    {loading.candidates ? (
                      <Skeleton
                        variant="text"
                        width={80}
                        sx={{ bgcolor: "rgba(255,255,255,0.3)" }}
                      />
                    ) : (
                      stat.value
                    )}
                  </Typography>
                </CardContent>
              </Card>
            ))}
          </Box>
          <Grid container spacing={3}>
            <Grid item xs={12} lg={8}>
              <Paper
                variant="outlined"
                sx={{
                  borderRadius: 3,
                  backgroundColor: theme.palette.mint?.main,
                  display: { xs: "none", md: "block" },
                  mb: { xs: 2, lg: 0 },
                  width: "100%",
                }}
              >
                <Typography variant="h6" sx={{ p: 2, pb: 1, fontWeight: 600 }}>
                  Applied Candidates
                </Typography>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow
                        sx={{
                          "& .MuiTableCell-root": {
                            bgcolor: "grey.50",
                            fontWeight: 600,
                            py: 1.5,
                            px: 2,
                          },
                        }}
                      >
                        <TableCell>Candidate</TableCell>
                        <TableCell>AI Match</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Applied Date</TableCell>
                        <TableCell align="right">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {loading.candidates ? (
                        Array.from(new Array(CANDIDATES_PER_PAGE)).map(
                          (_, index) => (
                            <TableRow key={index}>
                              <TableCell sx={{ py: 1.5, px: 2 }}>
                                <Stack
                                  direction="row"
                                  alignItems="center"
                                  spacing={2}
                                >
                                  <Skeleton
                                    variant="circular"
                                    width={40}
                                    height={40}
                                  />
                                  <Box>
                                    <Skeleton variant="text" width={120} />
                                    <Skeleton variant="text" width={80} />
                                  </Box>
                                </Stack>
                              </TableCell>
                              <TableCell>
                                <Skeleton variant="text" width={80} />
                              </TableCell>
                              <TableCell>
                                <Skeleton variant="text" width={100} />
                              </TableCell>
                              <TableCell>
                                <Skeleton variant="text" width={90} />
                              </TableCell>
                              <TableCell align="right">
                                <Stack
                                  direction="row"
                                  spacing={1}
                                  justifyContent="flex-end"
                                >
                                  <Skeleton
                                    variant="circular"
                                    width={28}
                                    height={28}
                                  />
                                  <Skeleton
                                    variant="circular"
                                    width={28}
                                    height={28}
                                  />
                                  <Skeleton
                                    variant="circular"
                                    width={28}
                                    height={28}
                                  />
                                </Stack>
                              </TableCell>
                            </TableRow>
                          ),
                        )
                      ) : paginatedCandidates.length > 0 ? (
                        paginatedCandidates.map((candidate) => (
                          <TableRow
                            key={candidate.id}
                            hover
                            sx={{
                              "&:last-child td, &:last-child th": { border: 0 },
                            }}
                          >
                            <TableCell sx={{ py: 1.5, px: 2 }}>
                              <Stack
                                direction="row"
                                alignItems="center"
                                spacing={2}
                              >
                                <Avatar sx={{ bgcolor: "primary.light" }}>
                                  {candidate.name?.charAt(0)}
                                </Avatar>
                                <Box>
                                  <Typography
                                    variant="subtitle2"
                                    sx={{ fontWeight: 600 }}
                                  >
                                    {candidate.name}
                                  </Typography>
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                  >
                                    {candidate.location}
                                  </Typography>
                                </Box>
                              </Stack>
                            </TableCell>
                            <TableCell>
                              {aiScoreLoadingIds.includes(candidate.id) ||
                              candidate.match === null ? (
                                <Chip
                                  icon={
                                    <CircularProgress
                                      size={14}
                                      color="inherit"
                                    />
                                  }
                                  label="Scoring..."
                                  size="small"
                                  variant="outlined"
                                />
                              ) : (
                                <Chip
                                  icon={<Star sx={{ fontSize: 16 }} />}
                                  label={
                                    candidate.match < 0
                                      ? "Error"
                                      : `${candidate.match}%`
                                  }
                                  color={getMatchColor(candidate.match)}
                                  size="small"
                                  variant="outlined"
                                  sx={{ fontWeight: 600 }}
                                />
                              )}
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={candidate.status}
                                color={getStatusColor(candidate.status)}
                                size="small"
                                sx={{
                                  fontWeight: 600,
                                  textTransform: "capitalize",
                                }}
                              />
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                {candidate.appliedDate}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Stack
                                direction="row"
                                spacing={0.5}
                                justifyContent="flex-end"
                              >
                                <IconButton
                                  size="small"
                                  onClick={() => handleViewCandidate(candidate)}
                                  title="View Details"
                                >
                                  <Visibility fontSize="small" />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  onClick={() =>
                                    handleScheduleInterview(candidate)
                                  }
                                  title="Schedule Interview"
                                >
                                  <Schedule fontSize="small" />
                                </IconButton>
                                {candidate.resumeUrl && (
                                  <IconButton
                                    size="small"
                                    component={Link}
                                    href={candidate.resumeUrl}
                                    target="_blank"
                                    title="Download Resume"
                                  >
                                    <Download fontSize="small" />
                                  </IconButton>
                                )}
                              </Stack>
                            </TableCell>
                          </TableRow>
                        ))
                      ) : (
                        <TableRow>
                          <TableCell colSpan={5} align="center" sx={{ py: 8 }}>
                            <Typography color="text.secondary">
                              No candidates match the current filter.
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
                {candidatesPageCount > 1 && (
                  <Stack alignItems="center" sx={{ p: 2 }}>
                    <Pagination
                      count={candidatesPageCount}
                      page={candidatesPage}
                      onChange={(e, value) => setCandidatesPage(value)}
                      color="primary"
                    />
                  </Stack>
                )}
              </Paper>
              <Stack spacing={2} sx={{ display: { xs: "flex", md: "none" } }}>
                {loading.candidates ? (
                  Array.from(new Array(3)).map((_, index) => (
                    <Card
                      key={index}
                      variant="outlined"
                      sx={{ borderRadius: 2.5 }}
                    >
                      <CardContent sx={{ p: 2 }}>
                        <Stack spacing={2}>
                          <Stack
                            direction="row"
                            justifyContent="space-between"
                            alignItems="flex-start"
                          >
                            <Stack
                              direction="row"
                              alignItems="center"
                              spacing={2}
                              minWidth={0}
                            >
                              <Skeleton
                                variant="circular"
                                width={48}
                                height={48}
                              />
                              <Box minWidth={0}>
                                <Skeleton
                                  variant="text"
                                  width={150}
                                  height={28}
                                />
                                <Skeleton variant="text" width={100} />
                              </Box>
                            </Stack>
                            <Skeleton
                              variant="rectangular"
                              width={70}
                              height={24}
                              sx={{ borderRadius: "16px", mt: 0.5 }}
                            />
                          </Stack>
                          <Divider />
                          <Stack
                            direction="row"
                            justifyContent="space-between"
                            alignItems="center"
                          >
                            <Box>
                              <Skeleton variant="text" width={40} />
                              <Skeleton
                                variant="rectangular"
                                width={90}
                                height={22}
                                sx={{ borderRadius: "16px", mt: 0.5 }}
                              />
                            </Box>
                            <Box textAlign="right">
                              <Skeleton variant="text" width={50} />
                              <Skeleton
                                variant="text"
                                width={80}
                                sx={{ mt: 0.5 }}
                              />
                            </Box>
                          </Stack>
                          <Stack direction="row" spacing={1} pt={1}>
                            <Skeleton
                              variant="rectangular"
                              height={31}
                              sx={{ borderRadius: 2, width: "100%" }}
                            />
                            <Skeleton
                              variant="rectangular"
                              height={31}
                              sx={{ borderRadius: 2, width: "100%" }}
                            />
                            <Skeleton
                              variant="circular"
                              width={36}
                              height={36}
                            />
                          </Stack>
                        </Stack>
                      </CardContent>
                    </Card>
                  ))
                ) : paginatedCandidates.length > 0 ? (
                  paginatedCandidates.map((candidate) => (
                    <Card
                      key={candidate.id}
                      variant="outlined"
                      sx={{ borderRadius: 2.5 }}
                    >
                      <CardContent sx={{ p: 2 }}>
                        <Stack spacing={2}>
                          <Stack
                            direction="row"
                            justifyContent="space-between"
                            alignItems="flex-start"
                          >
                            <Stack
                              direction="row"
                              alignItems="center"
                              spacing={2}
                              minWidth={0}
                            >
                              <Avatar
                                sx={{
                                  bgcolor: "primary.light",
                                  width: 48,
                                  height: 48,
                                }}
                              >
                                {candidate.name?.charAt(0)}
                              </Avatar>
                              <Box minWidth={0}>
                                <Typography
                                  noWrap
                                  variant="h6"
                                  sx={{ fontWeight: 600 }}
                                >
                                  {candidate.name}
                                </Typography>
                                <Typography
                                  noWrap
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  {candidate.location}
                                </Typography>
                              </Box>
                            </Stack>
                          </Stack>
                          <Divider />
                          <Stack
                            direction="row"
                            justifyContent="space-between"
                            alignItems="center"
                          >
                            <Box>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                Status
                              </Typography>
                              <Chip
                                label={candidate.status}
                                color={getStatusColor(candidate.status)}
                                size="small"
                                sx={{
                                  fontWeight: 600,
                                  textTransform: "capitalize",
                                  display: "flex",
                                  mt: 0.5,
                                }}
                              />
                            </Box>
                            <Box textAlign="right">
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                Applied
                              </Typography>
                              <Typography
                                variant="body2"
                                sx={{ fontWeight: 500 }}
                              >
                                {candidate.appliedDate}
                              </Typography>
                            </Box>
                          </Stack>
                          <Stack direction="row" spacing={1} pt={1}>
                            <Button
                              fullWidth
                              variant="contained"
                              size="small"
                              onClick={() => handleViewCandidate(candidate)}
                              startIcon={<Visibility />}
                              sx={{ borderRadius: 2 }}
                            >
                              Details
                            </Button>
                            <Button
                              fullWidth
                              variant="outlined"
                              size="small"
                              onClick={() => handleScheduleInterview(candidate)}
                              startIcon={<Schedule />}
                              sx={{ borderRadius: 2 }}
                            >
                              Schedule
                            </Button>
                            {candidate.resumeUrl && (
                              <IconButton
                                component={Link}
                                href={candidate.resumeUrl}
                                target="_blank"
                              >
                                <Download />
                              </IconButton>
                            )}
                          </Stack>
                        </Stack>
                      </CardContent>
                    </Card>
                  ))
                ) : (
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 4,
                      textAlign: "center",
                      borderRadius: 3,
                      bgcolor: "grey.50",
                    }}
                  >
                    <Typography color="text.secondary">
                      No candidates match the current filter.
                    </Typography>
                  </Paper>
                )}
                {candidatesPageCount > 1 && (
                  <Stack alignItems="center" sx={{ pt: 1 }}>
                    <Pagination
                      count={candidatesPageCount}
                      page={candidatesPage}
                      onChange={(e, value) => setCandidatesPage(value)}
                      color="primary"
                    />
                  </Stack>
                )}
              </Stack>
            </Grid>
            <Grid item xs={12} lg={4}>
              <Paper
                variant="outlined"
                sx={{
                  borderRadius: 3,
                  height: "100%",
                  backgroundColor: theme.palette.mint?.main,
                  width: "100%",
                  maxWidth: 650,
                }}
              >
                <Typography variant="h6" sx={{ p: 2, pb: 1, fontWeight: 600 }}>
                  Potential Candidates
                </Typography>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow
                        sx={{
                          "& .MuiTableCell-root": {
                            bgcolor: "grey.50",
                            fontWeight: 600,
                            py: 1.5,
                            px: 2,
                          },
                        }}
                      >
                        <TableCell>Candidate</TableCell>
                        <TableCell>AI Match</TableCell>
                        <TableCell align="right">Resume</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {loading.potentialCandidates ? (
                        Array.from(
                          new Array(POTENTIAL_CANDIDATES_PER_PAGE),
                        ).map((_, index) => (
                          <TableRow key={index}>
                            <TableCell sx={{ py: 1, px: 2 }}>
                              <Stack
                                direction="row"
                                alignItems="center"
                                spacing={2}
                              >
                                <Skeleton
                                  variant="circular"
                                  width={40}
                                  height={40}
                                />
                                <Box>
                                  <Skeleton variant="text" width={100} />
                                  <Skeleton variant="text" width={70} />
                                </Box>
                              </Stack>
                            </TableCell>
                            <TableCell>
                              <Skeleton variant="text" width={50} />
                            </TableCell>
                            <TableCell align="right">
                              <Skeleton
                                variant="rectangular"
                                width={80}
                                height={30}
                                sx={{ borderRadius: "4px" }}
                              />
                            </TableCell>
                          </TableRow>
                        ))
                      ) : paginatedPotentialCandidates.length > 0 ? (
                        paginatedPotentialCandidates.map((talent) => (
                          <TableRow
                            key={talent.talent_id}
                            hover
                            sx={{
                              "&:last-child td, &:last-child th": { border: 0 },
                            }}
                          >
                            <TableCell sx={{ py: 1, px: 2 }}>
                              <Stack
                                direction="row"
                                alignItems="center"
                                spacing={1.5}
                              >
                                <Avatar sx={{ bgcolor: "secondary.light" }}>
                                  {talent.name?.charAt(0)}
                                </Avatar>
                                <Box>
                                  <Typography
                                    variant="subtitle2"
                                    sx={{ fontWeight: 600 }}
                                  >
                                    {talent.name}
                                  </Typography>
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                  >
                                    {talent.location || "N/A"}
                                  </Typography>
                                </Box>
                              </Stack>
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={`${Math.round(talent.ai_match_score)}%`}
                                color={getMatchColor(talent.ai_match_score)}
                                size="small"
                                variant="outlined"
                                sx={{ fontWeight: 600 }}
                              />
                            </TableCell>
                            <TableCell align="right">
                              <Button
                                variant="outlined"
                                size="small"
                                component={Link}
                                href={talent.resume_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                disabled={!talent.resume_url}
                                sx={{ textTransform: "none" }}
                              >
                                Resume
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))
                      ) : (
                        <TableRow>
                          <TableCell colSpan={3} align="center" sx={{ py: 8 }}>
                            <Typography color="text.secondary">
                              No potential candidates found.
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
                {potentialCandidatesPageCount > 1 && (
                  <Stack alignItems="center" sx={{ p: 2 }}>
                    <Pagination
                      count={potentialCandidatesPageCount}
                      page={potentialCandidatesPage}
                      onChange={(e, value) => setPotentialCandidatesPage(value)}
                      color="secondary"
                    />
                  </Stack>
                )}
              </Paper>
            </Grid>
          </Grid>
        </>
      </Box>
    );
  };

  const renderCandidateDialog = () => (
    <Dialog
      open={openDialog}
      onClose={() => setOpenDialog(false)}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 4,
          height: { md: "calc(100% - 64px)" },
          backgroundColor: theme.palette.mint?.main,
        },
      }}
    >
      {selectedCandidate && (
        <>
          <DialogTitle sx={{ p: { xs: 2, sm: 3 } }}>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              justifyContent="space-between"
              alignItems={{ xs: "flex-start", sm: "center" }}
              spacing={{ xs: 2, sm: 1 }}
            >
              <Stack direction="row" spacing={2} alignItems="center">
                <Avatar
                  sx={{ width: 56, height: 56, bgcolor: "primary.light" }}
                >
                  {selectedCandidate.name?.charAt(0)}
                </Avatar>
                <Box>
                  <Typography variant="h5" sx={{ fontWeight: 600 }}>
                    {selectedCandidate.name}
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    Applied for: {selectedCandidate.position}
                  </Typography>
                </Box>
              </Stack>
            </Stack>
          </DialogTitle>
          <DialogContent dividers sx={{ p: 0, border: "none" }}>
            <Tabs
              value={tabValue}
              onChange={(e, newValue) => setTabValue(newValue)}
              variant="fullWidth"
              sx={{
                borderBottom: 1,
                borderColor: "divider",
                "& .MuiTab-root": { textTransform: "none", fontWeight: 600 },
              }}
            >
              <Tab label="Profile" />
              <Tab label="Schedule Interview" />
            </Tabs>
            <Box sx={{ p: { xs: 2, sm: 3 }, overflowY: "auto" }}>
              {loading.details ? (
                <Grid container spacing={3}>
                  <Grid item xs={12} md={5}>
                    <Skeleton
                      variant="text"
                      width="60%"
                      height={32}
                      sx={{ mb: 2 }}
                    />
                    <Stack spacing={2}>
                      <Stack direction="row" spacing={1.5} alignItems="center">
                        <Skeleton variant="circular" width={24} height={24} />
                        <Skeleton variant="text" width="80%" />
                      </Stack>
                      <Stack direction="row" spacing={1.5} alignItems="center">
                        <Skeleton variant="circular" width={24} height={24} />
                        <Skeleton variant="text" width="70%" />
                      </Stack>
                      <Stack direction="row" spacing={1.5} alignItems="center">
                        <Skeleton variant="circular" width={24} height={24} />
                        <Skeleton variant="text" width="90%" />
                      </Stack>
                    </Stack>
                    <Divider sx={{ my: 3 }} />
                    <Skeleton
                      variant="text"
                      width="70%"
                      height={32}
                      sx={{ mb: 2 }}
                    />
                    <Stack direction="row" spacing={1.5}>
                      <Skeleton
                        variant="rectangular"
                        width={110}
                        height={36}
                        sx={{ borderRadius: 2 }}
                      />
                      <Skeleton
                        variant="rectangular"
                        width={110}
                        height={36}
                        sx={{ borderRadius: 2 }}
                      />
                    </Stack>
                  </Grid>
                  <Grid item xs={12} md={7}>
                    <Skeleton
                      variant="text"
                      width="80%"
                      height={32}
                      sx={{ mb: 2 }}
                    />
                    <Skeleton variant="text" />
                    <Skeleton variant="text" />
                    <Skeleton variant="text" width="90%" />
                    <Divider sx={{ my: 3 }} />
                    <Skeleton
                      variant="text"
                      width="40%"
                      height={32}
                      sx={{ mb: 2 }}
                    />
                    <Box display="flex" flexWrap="wrap" gap={1}>
                      <Skeleton
                        variant="rectangular"
                        width={70}
                        height={24}
                        sx={{ borderRadius: "16px" }}
                      />
                      <Skeleton
                        variant="rectangular"
                        width={90}
                        height={24}
                        sx={{ borderRadius: "16px" }}
                      />
                      <Skeleton
                        variant="rectangular"
                        width={60}
                        height={24}
                        sx={{ borderRadius: "16px" }}
                      />
                      <Skeleton
                        variant="rectangular"
                        width={80}
                        height={24}
                        sx={{ borderRadius: "16px" }}
                      />
                    </Box>
                  </Grid>
                </Grid>
              ) : (
                <>
                  {tabValue === 0 && (
                    <Grid container spacing={3}>
                      <Grid item xs={12} md={5}>
                        <Typography
                          variant="h6"
                          sx={{ mb: 2, fontWeight: 600 }}
                        >
                          Contact & Info
                        </Typography>
                        <Stack spacing={2}>
                          <Stack
                            direction="row"
                            spacing={1.5}
                            alignItems="center"
                          >
                            <Email color="action" />
                            <Typography>{selectedCandidate.email}</Typography>
                          </Stack>
                          <Stack
                            direction="row"
                            spacing={1.5}
                            alignItems="center"
                          >
                            <Phone color="action" />
                            <Typography>{selectedCandidate.phone}</Typography>
                          </Stack>
                          <Stack
                            direction="row"
                            spacing={1.5}
                            alignItems="center"
                          >
                            <LocationOn color="action" />
                            <Typography>
                              {selectedCandidate.location}
                            </Typography>
                          </Stack>
                        </Stack>
                        <Divider sx={{ my: 3 }} />
                        <Typography
                          variant="h6"
                          sx={{ mb: 2, fontWeight: 600 }}
                        >
                          Documents & Links
                        </Typography>
                        <Stack direction="row" spacing={1.5}>
                          {selectedCandidate.resumeUrl && (
                            <Button
                              variant="contained"
                              startIcon={<Download />}
                              component={Link}
                              href={selectedCandidate.resumeUrl}
                              target="_blank"
                              sx={{ borderRadius: 2 }}
                            >
                              Resume
                            </Button>
                          )}
                          {selectedCandidate.portfolioUrl && (
                            <Button
                              variant="outlined"
                              startIcon={<Visibility />}
                              component={Link}
                              href={selectedCandidate.portfolioUrl}
                              target="_blank"
                              sx={{ borderRadius: 2 }}
                            >
                              Portfolio
                            </Button>
                          )}
                        </Stack>
                      </Grid>
                      <Grid item xs={12} md={7}>
                        <Typography
                          variant="h6"
                          sx={{ mb: 2, fontWeight: 600 }}
                        >
                          Professional Summary
                        </Typography>
                        <Typography
                          paragraph
                          variant="body2"
                          color="text.secondary"
                        >
                          {selectedCandidate.experience}
                        </Typography>
                        <Divider sx={{ my: 3 }} />
                        <Typography
                          variant="h6"
                          sx={{ mb: 2, fontWeight: 600 }}
                        >
                          Skills
                        </Typography>
                        <Box display="flex" flexWrap="wrap" gap={1}>
                          {selectedCandidate.skills?.length > 0 ? (
                            selectedCandidate.skills.map((skill, index) => (
                              <Chip
                                key={index}
                                label={skill}
                                variant="outlined"
                                size="small"
                              />
                            ))
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              No skills specified
                            </Typography>
                          )}
                        </Box>
                      </Grid>
                    </Grid>
                  )}
                  {tabValue === 1 && (
                    <Stack spacing={3}>
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        Schedule Interview
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            type="date"
                            label="Date"
                            value={interviewData.date}
                            onChange={(e) =>
                              setInterviewData((p) => ({
                                ...p,
                                date: e.target.value,
                              }))
                            }
                            InputLabelProps={{ shrink: true }}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            type="time"
                            label="Time"
                            value={interviewData.time}
                            onChange={(e) =>
                              setInterviewData((p) => ({
                                ...p,
                                time: e.target.value,
                              }))
                            }
                            InputLabelProps={{ shrink: true }}
                          />
                        </Grid>
                      </Grid>
                      <FormControl fullWidth>
                        <InputLabel>Type</InputLabel>
                        <Select
                          value={interviewData.type}
                          onChange={(e) =>
                            setInterviewData((p) => ({
                              ...p,
                              type: e.target.value,
                            }))
                          }
                          label="Type"
                        >
                          <MenuItem value="video">Video Call</MenuItem>
                          <MenuItem value="in-person">In-Person</MenuItem>
                        </Select>
                      </FormControl>
                      {interviewData.type === "video" && (
                        <TextField
                          fullWidth
                          label="Video Call Link"
                          value={interviewData.link}
                          onChange={(e) =>
                            setInterviewData((p) => ({
                              ...p,
                              link: e.target.value,
                            }))
                          }
                          placeholder="e.g., https://meet.google.com/xyz-abc-def"
                        />
                      )}
                      {interviewData.type === "in-person" && (
                        <>
                          <TextField
                            fullWidth
                            label="Address"
                            value={interviewData.address}
                            onChange={(e) =>
                              setInterviewData((p) => ({
                                ...p,
                                address: e.target.value,
                              }))
                            }
                            placeholder="e.g., 123 Main St, Anytown, USA"
                          />
                          <TextField
                            fullWidth
                            label="Map Link (Optional)"
                            value={interviewData.mapUrl}
                            onChange={(e) =>
                              setInterviewData((p) => ({
                                ...p,
                                mapUrl: e.target.value,
                              }))
                            }
                            placeholder="e.g., https://maps.app.goo.gl/..."
                          />
                        </>
                      )}
                      <TextField
                        fullWidth
                        multiline
                        rows={4}
                        label="Notes for Interviewer (Optional)"
                        value={interviewData.notes}
                        onChange={(e) =>
                          setInterviewData((p) => ({
                            ...p,
                            notes: e.target.value,
                          }))
                        }
                      />
                    </Stack>
                  )}
                </>
              )}
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: { xs: 2, sm: 3 }, gap: 1 }}>
            {tabValue === 0 && (
              <Button
                variant="contained"
                color="primary"
                onClick={handleShortlistCandidate}
                startIcon={<BookmarkBorderIcon />}
                sx={{ borderRadius: 2 }}
              >
                Shortlist
              </Button>
            )}
            <Button
              onClick={() => setOpenDialog(false)}
              variant="outlined"
              sx={{ borderRadius: 2 }}
            >
              Close
            </Button>
            {tabValue === 1 && (
              <Button
                variant="contained"
                onClick={handleInterviewSubmit}
                startIcon={<Schedule />}
                sx={{ borderRadius: 2 }}
              >
                Schedule Interview
              </Button>
            )}
          </DialogActions>
        </>
      )}
    </Dialog>
  );

  return (
    <Layout title="Candidate Management">
      {selectedJobPost ? renderCandidateListView() : renderJobPostListView()}
      {renderCandidateDialog()}
      {renderDraftJobDialog()}
    </Layout>
  );
};

export default CandidateManagement;
